import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import type { ReactElement } from 'react';

/**
 * Accessibility conformance statement (`/trust/accessibility`) —
 * launch-readiness B5.
 *
 * Follows the W3C accessibility-statement shape (scope → standard →
 * status → measures → evaluation method → limitations → feedback), with
 * HONEST per-surface status: the embeddable widget is the conformant
 * surface (designed, reviewed, and regression-tested to WCAG 2.1 AA);
 * the dashboard and this site are partially conformant with AA as the
 * engineering bar. Procurement reviewers read these statements against
 * an audit — over-claiming here costs more than a "partial" ever will.
 *
 * Content authority: `.claude/rules/ui-design.md` § Accessibility (the
 * AA bar) + the widget's enforced contrast/keyboard/RTL floors. When the
 * enforcement changes, this page changes in the same PR.
 */
export function Accessibility(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('a11ystmt.meta.title'),
    description: t('a11ystmt.meta.description'),
    path: '/trust/accessibility',
  });
  return (
    <LegalPage
      breadcrumb={t('a11ystmt.breadcrumb')}
      title={t('a11ystmt.heading')}
      intro={t('a11ystmt.intro')}
    >
      <section aria-labelledby="a11y-status" className="mb-10">
        <h2
          id="a11y-status"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('a11ystmt.status.heading')}
        </h2>
        <dl className="space-y-4">
          <div data-surface="widget">
            <dt className="font-semibold">{t('a11ystmt.status.widget.name')}</dt>
            <dd className="text-[color:var(--bd-muted)]">{t('a11ystmt.status.widget.body')}</dd>
          </div>
          <div data-surface="dashboard">
            <dt className="font-semibold">{t('a11ystmt.status.dashboard.name')}</dt>
            <dd className="text-[color:var(--bd-muted)]">{t('a11ystmt.status.dashboard.body')}</dd>
          </div>
          <div data-surface="site">
            <dt className="font-semibold">{t('a11ystmt.status.site.name')}</dt>
            <dd className="text-[color:var(--bd-muted)]">{t('a11ystmt.status.site.body')}</dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="a11y-measures" className="mb-10">
        <h2
          id="a11y-measures"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('a11ystmt.measures.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('a11ystmt.measures.contrast')}</li>
          <li>{t('a11ystmt.measures.keyboard')}</li>
          <li>{t('a11ystmt.measures.rtl')}</li>
          <li>{t('a11ystmt.measures.semantics')}</li>
          <li>{t('a11ystmt.measures.motion')}</li>
        </ul>
      </section>

      <section aria-labelledby="a11y-method" className="mb-10">
        <h2
          id="a11y-method"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('a11ystmt.method.heading')}
        </h2>
        <p>{t('a11ystmt.method.body')}</p>
      </section>

      <section aria-labelledby="a11y-limits" className="mb-10">
        <h2
          id="a11y-limits"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('a11ystmt.limitations.heading')}
        </h2>
        <ul className="list-disc space-y-1 ps-6">
          <li>{t('a11ystmt.limitations.dashboardAudit')}</li>
          <li>{t('a11ystmt.limitations.hostPage')}</li>
        </ul>
      </section>

      <section aria-labelledby="a11y-feedback" className="mb-4">
        <h2
          id="a11y-feedback"
          className="mb-3 mt-11 scroll-mt-[calc(var(--bd-nav-h)+22px)] text-[1.45rem] font-semibold leading-[1.15] tracking-[-0.032em] text-[color:var(--bd-ink)]"
        >
          {t('a11ystmt.feedback.heading')}
        </h2>
        <p>
          {t('a11ystmt.feedback.body')}{' '}
          <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
            admin@syncanix.com
          </a>
          .
        </p>
      </section>

      <p className="text-sm text-[color:var(--bd-muted)]">{t('a11ystmt.updated')}</p>
    </LegalPage>
  );
}
